import _ from 'underscore';
import Promise from 'bluebird';
import { FilterStorageFactory, WaterfallFilterStorage, WaterfallFilterType } from '@bingads-webui-campaign/filter-storage';
import { SchemaI18n } from '@bingads-webui-universal/schema-i18n';
import {
  updateEntitySchemaForPilotFeatures,
  enableFilterForExtraPerformanceMetrics,
  updateEntitySchemaForPartialConversions,
  disableFilterForExtraPerformanceMetrics,
  initialize as initializeCommonFilterConfig,
} from './util/update-entity-schema-for-pilot-features';
import {
  KeywordSchemaProcessor,
  AdGroupSchemaProcessor,
  AdSchemaProcessor,
  DayTimeCriterionSchemaProcessor,
  DeviceCriterionSchemaProcessor,
  LocationCriterionSchemaProcessor,
  NegativeKeywordSchemaProcessor,
  ExperimentSchemaProcessor,
  AssetLinkSchemaProcessor,
  AssetWithBIDataSchemaProcessor,
  ResponsiveSearchAdInstanceSchemaProcessor,
  BaseCriterionSchemaProcessor,
  AudienceCriterionWithBISchemaProcessor,
  AudienceSchemaProcessor,
  ProductSchemaProcessor,
  GMBImportSchemaProcessor,
  SearchTermSchemaProcessor,
  PerformanceSchemaProcessor,
  SmartPerformanceSchemaProcessor,
  ManagementSchemaProcessor,
  AccountImportSchemaProcessor,
  TagSchemaProcessor,
  LinkedStoreSchemaProcessor,
  GoalSchemaProcessor,
  SeasonalityAdjustmentSchemaProcessor,
  DataExclusionSchemaProcessor,
  SharedBudgetSchemaProcessor,
  SharedBudgetCampaignSchemaProcessor,
  SelAccountLevelListsGridSchemaProcessor,
  SelCustomerLevelAssociationsGridSchemaProcessor,
  HotelCenterOverviewSchemaProcessor,
  AssetGroupSchemaProcessor,
  PmaxAssetLinkSchemaProcessor,
  UnifiedAdsAssetLinkSchemaProcessor,
  AppAdsAssetGroupSchemaProcessor,
} from './schema-processors/index';

const featureToSchemaProcessorMapping = {
  AutomatedRuleKeywordGrid: KeywordSchemaProcessor,
  AutomatedRuleAdgroupGrid: AdGroupSchemaProcessor,
  AutomatedRuleAdGrid: AdSchemaProcessor,
  KeywordGrid: KeywordSchemaProcessor,
  AdgroupGrid: AdGroupSchemaProcessor,
  AdGrid: AdSchemaProcessor,
  AdScheduleGrid: DayTimeCriterionSchemaProcessor,
  LocationsGrid: LocationCriterionSchemaProcessor,
  ExcludedLocationsGrid: BaseCriterionSchemaProcessor,
  DevicesGrid: DeviceCriterionSchemaProcessor,
  campaignNegativeKeywordGrid: NegativeKeywordSchemaProcessor,
  adgroupNegativeKeywordGrid: NegativeKeywordSchemaProcessor,
  ExperimentsGrid: ExperimentSchemaProcessor,
  RSAAssetsGrid: AssetLinkSchemaProcessor,
  AssetsReportGrid: AssetWithBIDataSchemaProcessor,
  RSACombinationsGrid: ResponsiveSearchAdInstanceSchemaProcessor,
  AudienceAssociationGrid: AudienceCriterionWithBISchemaProcessor,
  AudienceGrid: AudienceSchemaProcessor,
  ProductGrid: ProductSchemaProcessor,
  GmbImportV2: GMBImportSchemaProcessor,
  SearchTermGrid: SearchTermSchemaProcessor,
  PerformanceGrid: PerformanceSchemaProcessor,
  SmartPerformanceGrid: SmartPerformanceSchemaProcessor,
  ManagementGrid: ManagementSchemaProcessor,
  ImportGrid: AccountImportSchemaProcessor,
  UETTagsGrid: TagSchemaProcessor,
  LinkedStoreGrid: LinkedStoreSchemaProcessor,
  ConversionGoalsGrid: GoalSchemaProcessor,
  SharedBudgetSummaryGrid: SharedBudgetSchemaProcessor,
  SharedBudgetEditGrid: SharedBudgetCampaignSchemaProcessor,
  AccountLevelListGrid: SelAccountLevelListsGridSchemaProcessor,
  CustomerLevelEditAccountsGrid: SelCustomerLevelAssociationsGridSchemaProcessor,
  HotelCenterOverviewGridHotelSubAccountGrid: HotelCenterOverviewSchemaProcessor,
  HotelCenterOverviewGridHotelGroupGrid: HotelCenterOverviewSchemaProcessor,
  HotelCenterOverviewGridHotelIdGrid: HotelCenterOverviewSchemaProcessor,
  HotelCenterOverviewGridHotelDeviceTypeGrid: HotelCenterOverviewSchemaProcessor,
  HotelCenterOverviewGridHotelDateGrid: HotelCenterOverviewSchemaProcessor,
  HotelCenterOverviewGridHotelLengthOfStayGrid: HotelCenterOverviewSchemaProcessor,
  HotelCenterOverviewGridHotelUserCountryGrid: HotelCenterOverviewSchemaProcessor,
  HotelCenterOverviewGridHotelSlotTypeGrid: HotelCenterOverviewSchemaProcessor,
  HotelCenterOverviewGridHotelCountryGrid: HotelCenterOverviewSchemaProcessor,
  HotelCenterOverviewGridAdvancedBookingGrid: HotelCenterOverviewSchemaProcessor,
  HotelCenterOverviewGridCheckinDayGrid: HotelCenterOverviewSchemaProcessor,
  HotelCenterOverviewGridDateTypeGrid: HotelCenterOverviewSchemaProcessor,
  HotelCenterOverviewGridSiteTypeGrid: HotelCenterOverviewSchemaProcessor,
  SeasonalityAdjustmentGrid: SeasonalityAdjustmentSchemaProcessor,
  DataExclusionGrid: DataExclusionSchemaProcessor,
  AssetGroupGrid: AssetGroupSchemaProcessor,
  AppAdsAssetGroupGrid: AppAdsAssetGroupSchemaProcessor,
  PmaxAssetGrid: PmaxAssetLinkSchemaProcessor,
  UnifiedAdsAssetGrid: UnifiedAdsAssetLinkSchemaProcessor,
};

function mergeFilters(filters) {
  if (filters.length > 1) {
    return {
      filter: {
        key: filters[0].filter.key,
        value: {
          query: {
            $and: _.map(filters, filterObj => filterObj.filter.value.query),
          },
        },
      },
    };
  } else if (filters.length === 1) {
    return filters[0];
  }
  return null;
}

function getWaterfallFilterStoreVNext(filterKeys, preferenceService, featureName) {
  const waterfallFilterStorage = new WaterfallFilterStorage({
    preferenceService,
  });

  return {
    get: () => {
      const waterfallFilters = waterfallFilterStorage.getFilters(filterKeys);

      if (!_.isArray(waterfallFilters)) {
        return Promise.resolve(null);
      }

      const filters = [];

      _.each(filterKeys, (filterKey, i) => {
        if (!waterfallFilters[i] || !waterfallFilters[i].filter.value.query) {
          return;
        }

        if (featureName === 'campaignNegativeKeywordGrid' && filterKey === WaterfallFilterType.AdGroupStatus) {
          return;
        }

        // If current grid is on the same level as the waterfall filter, ex. filtering by
        // ad group status in ad group grid, need to change filter's property name from
        // AdGroupStatus to Status. Need to do the same for campaign grid.
        if (featureName === 'AdgroupGrid' && filterKey === WaterfallFilterType.AdGroupStatus) {
          filters.push({
            filter: {
              key: WaterfallFilterType.AdGroupStatus,
              value: {
                query: {
                  Status: waterfallFilters[i].filter.value.query.AdGroupStatus,
                },
              },
            },
          });
        } else if ((featureName === 'AllSettingsGrid') && filterKey === WaterfallFilterType.CampaignStatus) {
          filters.push({
            filter: {
              key: WaterfallFilterType.CampaignStatus,
              value: {
                query: {
                  Status: waterfallFilters[i].filter.value.query.CampaignStatus,
                },
              },
            },
          });
        } else {
          filters.push(waterfallFilters[i]);
        }
      });
      return Promise.resolve(mergeFilters(filters));
    },
  };
}

export function getCommonFilterConfig({
  featureName,
  schemaProcessor,
  searchbox,
  editor,
  selector,
  enableCampaignWaterfall = false,
  enableAdGroupWaterfall = false,
  enableAvgPositionDeprecation = true,
  allowCampaignStatusWaterfall = true,
  allowCampaignTypeWaterfall = true,
  enableEditorialRejectionFilter = false,
  i18n,
  entitySchema,
  cachedEntitySchema,
  filterExpressions = null,
  sessionStore,
  preferenceStoreFactory,
  globalConfig,
  legacyGlobalI18n,
  schemaI18nCampaign,
  cacheService,
  dataContextManager,
  jsdata,
  getLegacySaved = true,
  updatePilot = true,
  preferenceService,
  customColumnPreference,
  odata = {
    ignoreCase: false,
    custom: {},
  },
  edm,
  state,
  extraPerformanceColumns,
  disabledPerformanceColumns,
  scope,
  extraConfigForSchemaProcessor = {},
}) {
  const schemaConfigOptions = {
    globalConfig,
    schemaI18nCampaign,
    i18n: legacyGlobalI18n,
    cacheService,
    dataContextManager,
    jsdata,
    isForVNext: true,
    passedInI18n: i18n,
    enableEditorialRejectionFilter,
    preferenceService,
    customColumnPreference,
    featureName,
    edm,
    state,
    scope,
    extraConfigForSchemaProcessor,
  };

  let updatedEntitySchema = null;

  if (!cachedEntitySchema) {
    updatedEntitySchema = entitySchema;

    if (updatePilot) {
      initializeCommonFilterConfig(schemaConfigOptions);
      updatedEntitySchema = updateEntitySchemaForPilotFeatures(entitySchema, /* isForVNext */true, filterExpressions, enableAvgPositionDeprecation); // eslint-disable-line max-len
    }

    if (_.isArray(extraPerformanceColumns) && !_.isEmpty(extraPerformanceColumns)) {
      enableFilterForExtraPerformanceMetrics(updatedEntitySchema, extraPerformanceColumns);
    }

    if (_.isArray(disabledPerformanceColumns) && !_.isEmpty(disabledPerformanceColumns)) {
      disableFilterForExtraPerformanceMetrics(updatedEntitySchema, disabledPerformanceColumns);
    }

    if (updatePilot) {
      updateEntitySchemaForPartialConversions(updatedEntitySchema);
    }
  } else {
    updatedEntitySchema = cachedEntitySchema;
  }

  const maxSavedFilterLimit = 10;
  const enabled = [];
  let campaignGridFilterStore;
  let adgroupFilterStore;

  if (enableCampaignWaterfall) {
    const waterfallFilterTypes = [];
    if (allowCampaignTypeWaterfall) {
      waterfallFilterTypes.push(WaterfallFilterType.CampaignType);
    }
    // Campaign grid will treat status filter as quick filter
    if (allowCampaignStatusWaterfall && featureName !== 'CampaignsGrid') {
      waterfallFilterTypes.push(WaterfallFilterType.CampaignStatus);
    }
    campaignGridFilterStore = getWaterfallFilterStoreVNext(
      waterfallFilterTypes,
      preferenceService,
      featureName
    );
  }

  if (enableAdGroupWaterfall) {
    adgroupFilterStore = getWaterfallFilterStoreVNext(
      [WaterfallFilterType.AdGroupStatus],
      preferenceService,
      featureName
    );
  }


  let baseConfig = {
    getLegacySaved,
    entitySchema: updatedEntitySchema,
    schemaI18n: new SchemaI18n({ i18n }),
    i18n,
    // won't work for kendo datepicker
    // shortDateFormat: 'yMd',
    odata,
    maxSavedFilterLimit,
    storageFactory: new FilterStorageFactory({
      getLegacySaved,
      sessionStore,
      preferenceStoreFactory,
      featureName,
      maxSavedFilterLimit,
      preferenceService,
    }),
    featureName,
    edm,
    scope,
  };

  if (enableCampaignWaterfall && enableAdGroupWaterfall) {
    baseConfig = _.extend(baseConfig, {
      waterfallFilterStores: [campaignGridFilterStore, adgroupFilterStore],
    });
  } else if (enableCampaignWaterfall) {
    baseConfig = _.extend(baseConfig, {
      waterfallFilterStores: [campaignGridFilterStore],
      waterfallEvent: 'adGroupWaterfall',
    });
  }

  let schemaProcessorCopy = schemaProcessor;
  if (!_.isFunction(schemaProcessor) && featureToSchemaProcessorMapping[featureName]) {
    schemaProcessorCopy = new featureToSchemaProcessorMapping[featureName](schemaConfigOptions);
  }

  if (schemaProcessorCopy && _.isFunction(schemaProcessorCopy.updateEntitySchemaForFeatures)) {
    baseConfig = _.extend(baseConfig, {
      schemaProcessor:
        schemaProcessorCopy.updateEntitySchemaForFeatures.bind(schemaProcessorCopy),
    });
  }

  if (schemaProcessorCopy && _.isFunction(schemaProcessorCopy.updateOdataOptions)) {
    baseConfig = _.extend(baseConfig, {
      odataOptionsProcessor: schemaProcessorCopy.updateOdataOptions.bind(schemaProcessorCopy),
    });
  }

  if (searchbox) {
    baseConfig = _.extend(baseConfig, {
      searchbox,
    });
    enabled.push('searchbox');
  }

  if (editor) {
    baseConfig = _.extend(baseConfig, {
      editor: _.extend({
        isComparingEnabled: () => window.App && window.App.state && !!window.App.state.get('$daterange').compared,
      }, editor),
    });
    enabled.push('editor');
  }

  if (selector) {
    baseConfig = _.extend(baseConfig, {
      selector,
    });
    enabled.push('selector');
  }

  baseConfig = _.extend(baseConfig, { enabled });
  return baseConfig;
}
